import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  DatasetsAPI,
  OdkAPI,
  AccumulatedDatasetItem,
  DatasetSource,
  DatasetDefinition,
} from "../api/endpoints";
import {
  PageHeader,
  Card,
  ErrorBanner,
  OkBanner,
  Spinner,
} from "../components/Layout";

export default function DatasetsPage() {
  const qc = useQueryClient();
  const [modalAbierto, setModalAbierto] = useState(false);
  const [datasetEditando, setDatasetEditando] = useState<AccumulatedDatasetItem | null>(null);
  const [datasetSeleccionado, setDatasetSeleccionado] = useState<number | null>(null);
  const [modalCasos, setModalCasos] = useState<number | null>(null);
  const [paginaCasos, setPaginaCasos] = useState(0);

  // Form state
  const [nombre, setNombre] = useState("");
  const [proyectoId, setProyectoId] = useState<number>(1);
  const [fuentes, setFuentes] = useState<DatasetSource[]>([]);
  const [actualizacionAutomatica, setActualizacionAutomatica] = useState(true);
  const [intervaloMinutos, setIntervaloMinutos] = useState(60);
  const [errorForm, setErrorForm] = useState<string | null>(null);

  const projectsQ = useQuery({ queryKey: ["projects"], queryFn: OdkAPI.projects });
  const formsQ = useQuery({
    queryKey: ["forms", proyectoId],
    queryFn: () => OdkAPI.forms(proyectoId),
    enabled: !!proyectoId,
  });

  const datasetsQ = useQuery({
    queryKey: ["datasets"],
    queryFn: DatasetsAPI.list,
    refetchInterval: (q) => {
      const hayActivo = (q.state.data ?? []).some(
        (d) => d.job && ["queued", "running", "retry"].includes(d.job.status)
      );
      return hayActivo ? 3000 : 15000;
    },
  });

  const detalleQ = useQuery({
    queryKey: ["dataset-detail", datasetSeleccionado],
    queryFn: () => DatasetsAPI.get(datasetSeleccionado!),
    enabled: !!datasetSeleccionado,
  });

  const casosQ = useQuery({
    queryKey: ["dataset-cases", modalCasos, paginaCasos],
    queryFn: () => DatasetsAPI.cases(modalCasos!, paginaCasos * 25, 25),
    enabled: !!modalCasos,
  });

  const refreshMutation = useMutation({
    mutationFn: (id: number) => DatasetsAPI.refresh(id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["datasets"] });
      void qc.invalidateQueries({ queryKey: ["dataset-detail"] });
    },
  });

  const guardarMutation = useMutation({
    mutationFn: async () => {
      if (!nombre.trim()) throw new Error("Escriba un nombre para el acumulado.");
      if (fuentes.length === 0) throw new Error("Agregue al menos una encuesta fuente.");
      const def: DatasetDefinition = {
        sources: fuentes,
        automatic: actualizacionAutomatica,
        interval_minutes: intervaloMinutos,
      };
      if (datasetEditando) {
        return DatasetsAPI.update(datasetEditando.id, {
          name: nombre.trim(),
          project_id: proyectoId,
          definition: def,
          expected_generation: datasetEditando.generation,
        });
      } else {
        return DatasetsAPI.create({
          name: nombre.trim(),
          project_id: proyectoId,
          definition: def,
        });
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["datasets"] });
      setModalAbierto(false);
      setDatasetEditando(null);
      setErrorForm(null);
    },
    onError: (e: Error) => {
      setErrorForm(e.message);
    },
  });

  const abrirCreacion = () => {
    setDatasetEditando(null);
    setNombre("");
    setProyectoId(projectsQ.data?.[0]?.id ?? 1);
    setFuentes([]);
    setActualizacionAutomatica(true);
    setIntervaloMinutos(60);
    setErrorForm(null);
    setModalAbierto(true);
  };

  const abrirEdicion = (d: AccumulatedDatasetItem) => {
    setDatasetEditando(d);
    setNombre(d.name);
    setProyectoId(d.project_id);
    const def = d.definition ?? { sources: [] };
    setFuentes(def.sources || []);
    setActualizacionAutomatica(def.automatic !== false);
    setIntervaloMinutos(def.interval_minutes || 60);
    setErrorForm(null);
    setModalAbierto(true);
  };

  const agregarFuente = (formId: string) => {
    if (!formId || fuentes.some((f) => f.form_id === formId)) return;
    const formObj = formsQ.data?.find((f) => f.xmlFormId === formId);
    setFuentes([
      ...fuentes,
      {
        project_id: proyectoId,
        form_id: formId,
        wave: formObj?.name || `Ola ${fuentes.length + 1}`,
        included: true,
        mappings: {},
      },
    ]);
  };

  const removerFuente = (idx: number) => {
    setFuentes(fuentes.filter((_, i) => i !== idx));
  };

  const actualizarOla = (idx: number, wave: string) => {
    const copia = [...fuentes];
    copia[idx].wave = wave;
    setFuentes(copia);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-tinta">Acumulados y Fusiones de Encuestas</h1>
          <p className="text-xs text-tinta-3 mt-1">
            Crea bases acumuladas fusionando trackings u olas periódicas. Funcionan como una encuesta
            más en todas las estadísticas del sistema.
          </p>
        </div>
        <button type="button" className="btn text-xs" onClick={abrirCreacion}>
          + Nuevo acumulado
        </button>
      </div>

      {refreshMutation.error && (
        <ErrorBanner message={(refreshMutation.error as Error).message} />
      )}

      {/* Listado de Acumulados */}
      <Card>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-sm font-semibold text-tinta uppercase tracking-wide">
              Bases acumuladas disponibles ({datasetsQ.data?.length ?? 0})
            </h2>
            {datasetsQ.isFetching && (
              <span className="text-xs text-marca flex items-center gap-1">
                <Spinner /> Sincronizando catálogo…
              </span>
            )}
          </div>

          {!datasetsQ.data || datasetsQ.data.length === 0 ? (
            <div className="p-8 text-center bg-plano rounded-lg border border-dashed border-grid">
              <p className="text-sm font-medium text-tinta">No hay acumulados creados aún.</p>
              <p className="text-xs text-tinta-3 mt-1 mb-4">
                Puedes fusionar varias olas para analizar evolución temporal o incrementar muestra.
              </p>
              <button type="button" className="btn-sec text-xs" onClick={abrirCreacion}>
                Crear el primer acumulado
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs border-collapse">
                <thead>
                  <tr className="border-b border-grid bg-superficie text-tinta-2">
                    <th className="p-2.5 font-medium">Nombre / Referencia</th>
                    <th className="p-2.5 font-medium">Proyecto</th>
                    <th className="p-2.5 font-medium">Casos</th>
                    <th className="p-2.5 font-medium">Versión</th>
                    <th className="p-2.5 font-medium">Estado del proceso</th>
                    <th className="p-2.5 font-medium text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-grid/60">
                  {datasetsQ.data.map((d) => {
                    const enProgreso = d.job && ["queued", "running", "retry"].includes(d.job.status);
                    return (
                      <tr key={d.id} className="hover:bg-plano/60 transition-colors">
                        <td className="p-2.5">
                          <div className="font-semibold text-tinta">{d.name}</div>
                          <div className="text-[11px] font-mono text-tinta-3 mt-0.5">
                            {d.form_id || "Sin corte publicado"}
                          </div>
                        </td>
                        <td className="p-2.5 text-tinta-2">
                          {projectsQ.data?.find((p) => p.id === d.project_id)?.name || `ID ${d.project_id}`}
                        </td>
                        <td className="p-2.5 font-medium text-tinta">
                          {d.n.toLocaleString()} entrevistas
                        </td>
                        <td className="p-2.5 text-tinta-2">
                          Rev {d.current_revision_id ?? "—"} (Gen {d.generation})
                        </td>
                        <td className="p-2.5">
                          {enProgreso ? (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[11px] font-medium bg-blue-50 text-blue-700 border border-blue-200">
                              <Spinner />
                              {d.job?.status === "queued" ? "En cola…" : "Procesando…"}
                            </span>
                          ) : d.job?.status === "failed" ? (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-red-50 text-red-700 border border-red-200" title={d.job?.error ?? ""}>
                              Falló actualización
                            </span>
                          ) : (
                            <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-green-50 text-green-700 border border-green-200">
                              ✓ Actualizado
                            </span>
                          )}
                        </td>
                        <td className="p-2.5 text-right space-x-1 whitespace-nowrap">
                          <button
                            type="button"
                            className="btn-sec text-[11px] px-2 py-1"
                            disabled={refreshMutation.isPending || !!enProgreso}
                            onClick={() => refreshMutation.mutate(d.id)}
                            title="Recalcular casos y actualizar desde fuentes ODK"
                          >
                            {enProgreso ? "Actualizando…" : "Actualizar"}
                          </button>
                          <button
                            type="button"
                            className="btn-sec text-[11px] px-2 py-1"
                            onClick={() => {
                              setModalCasos(d.id);
                              setPaginaCasos(0);
                            }}
                          >
                            Casos
                          </button>
                          <button
                            type="button"
                            className="btn-sec text-[11px] px-2 py-1"
                            onClick={() => setDatasetSeleccionado(datasetSeleccionado === d.id ? null : d.id)}
                          >
                            {datasetSeleccionado === d.id ? "Ocultar" : "Detalle"}
                          </button>
                          <button
                            type="button"
                            className="btn-sec text-[11px] px-2 py-1"
                            onClick={() => abrirEdicion(d)}
                          >
                            Editar
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Card>

      {/* Detalle del acumulado seleccionado */}
      {datasetSeleccionado && detalleQ.data && (
        <Card>
          <div className="space-y-4">
            <div className="flex items-center justify-between border-b border-grid pb-2">
              <h3 className="font-semibold text-sm text-tinta">
                Detalle y Fuentes: {detalleQ.data.name}
              </h3>
              <button
                type="button"
                className="text-xs text-tinta-3 hover:text-tinta"
                onClick={() => setDatasetSeleccionado(null)}
              >
                ✕ Cerrar detalle
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="text-xs font-semibold text-tinta uppercase tracking-wide mb-2">
                  Encuestas incluidas ({detalleQ.data.definition?.sources.length ?? 0})
                </h4>
                <div className="space-y-1.5">
                  {detalleQ.data.definition?.sources.map((s, idx) => (
                    <div key={idx} className="p-2 bg-plano rounded border border-grid text-xs flex justify-between items-center">
                      <div>
                        <span className="font-medium text-tinta">{s.wave}</span>
                        <div className="text-[11px] text-tinta-3">ID: {s.form_id}</div>
                      </div>
                      <span className="text-[11px] px-2 py-0.5 rounded bg-superficie border border-grid text-tinta-2">
                        {s.included ? "Activa" : "Excluida"}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="text-xs font-semibold text-tinta uppercase tracking-wide mb-2">
                  Historial de Revisiones Inmutables
                </h4>
                <div className="max-h-48 overflow-y-auto space-y-1.5 text-xs">
                  {detalleQ.data.revisions?.map((rev) => (
                    <div key={rev.id} className="p-2 bg-plano rounded border border-grid flex justify-between items-center">
                      <div>
                        <span className="font-medium text-tinta">Revisión #{rev.id}</span>
                        <div className="text-[11px] font-mono text-tinta-3">{rev.form_id}</div>
                      </div>
                      <div className="text-right">
                        <div className="font-semibold text-tinta">{rev.n.toLocaleString()} casos</div>
                        <div className="text-[11px] text-tinta-3">
                          {new Date(rev.created_at).toLocaleDateString()}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Modal Ver Casos */}
      {modalCasos !== null && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[85vh] flex flex-col border border-grid">
            <div className="p-4 border-b border-grid flex justify-between items-center">
              <h3 className="font-bold text-sm text-tinta">
                Casos del acumulado ({casosQ.data?.total ?? 0} registros)
              </h3>
              <button
                type="button"
                className="text-xs text-tinta-3 hover:text-tinta"
                onClick={() => setModalCasos(null)}
              >
                ✕ Cerrar
              </button>
            </div>
            <div className="p-4 overflow-y-auto flex-1 text-xs">
              {casosQ.isLoading ? (
                <div className="p-8 text-center text-tinta-3">Cargando casos…</div>
              ) : (
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="border-b border-grid text-tinta-2">
                      <th className="p-2">Identificador</th>
                      <th className="p-2">Formulario de origen</th>
                      <th className="p-2">Ola asignada</th>
                      <th className="p-2">Fecha</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-grid">
                    {casosQ.data?.cases.map((c, i) => (
                      <tr key={i} className="hover:bg-plano/40">
                        <td className="p-2 font-mono text-[11px] truncate max-w-xs">{c.key}</td>
                        <td className="p-2 text-tinta-2">{c.source}</td>
                        <td className="p-2 font-medium text-tinta">{c.wave}</td>
                        <td className="p-2 text-tinta-3">{c.date || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            <div className="p-3 border-t border-grid flex justify-between items-center text-xs">
              <button
                type="button"
                className="btn-sec text-xs"
                disabled={paginaCasos === 0}
                onClick={() => setPaginaCasos((p) => Math.max(0, p - 1))}
              >
                Anterior
              </button>
              <span className="text-tinta-3">Página {paginaCasos + 1}</span>
              <button
                type="button"
                className="btn-sec text-xs"
                disabled={(casosQ.data?.cases.length ?? 0) < 25}
                onClick={() => setPaginaCasos((p) => p + 1)}
              >
                Siguiente
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Crear / Editar */}
      {modalAbierto && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-grid p-6 space-y-4">
            <div className="flex justify-between items-center border-b border-grid pb-3">
              <h2 className="text-base font-bold text-tinta">
                {datasetEditando ? `Editar acumulado: ${datasetEditando.name}` : "Nuevo Acumulado de Encuestas"}
              </h2>
              <button
                type="button"
                className="text-xs text-tinta-3 hover:text-tinta"
                onClick={() => setModalAbierto(false)}
              >
                ✕ Cerrar
              </button>
            </div>

            {errorForm && <ErrorBanner message={errorForm} />}

            <div className="space-y-3 text-xs">
              <div>
                <label className="etiqueta">Nombre del acumulado *</label>
                <input
                  type="text"
                  className="input"
                  placeholder="ej. Tracking Presidencial (Olas 1 a 5)"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                />
              </div>

              <div>
                <label className="etiqueta">Proyecto ODK asociado *</label>
                <select
                  className="input"
                  disabled={!!datasetEditando}
                  value={proyectoId}
                  onChange={(e) => {
                    setProyectoId(Number(e.target.value));
                    setFuentes([]);
                  }}
                >
                  {projectsQ.data?.map((p) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {/* Fuentes añadidas */}
              <div className="pt-2 border-t border-grid space-y-2">
                <div className="flex justify-between items-center">
                  <label className="font-semibold text-tinta">Encuestas origen (Olas a fusionar):</label>
                </div>

                {fuentes.length === 0 ? (
                  <div className="p-3 bg-plano border border-grid rounded text-center text-tinta-3">
                    Aún no has agregado encuestas. Selecciona una en el selector inferior.
                  </div>
                ) : (
                  <div className="space-y-2">
                    {fuentes.map((f, i) => (
                      <div key={i} className="p-2.5 bg-plano rounded border border-grid flex items-center gap-3">
                        <div className="flex-1">
                          <div className="font-mono text-[11px] text-tinta-3">{f.form_id}</div>
                          <div className="flex items-center gap-2 mt-1">
                            <label className="text-[11px] text-tinta-2 font-medium">Nombre de la Ola:</label>
                            <input
                              type="text"
                              className="input py-0.5 text-xs flex-1"
                              value={f.wave}
                              onChange={(e) => actualizarOla(i, e.target.value)}
                            />
                          </div>
                        </div>
                        <button
                          type="button"
                          className="btn-sec text-xs text-estado-critico px-2 py-1"
                          onClick={() => removerFuente(i)}
                        >
                          Quitar
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="flex gap-2 items-center pt-2">
                  <select
                    className="input flex-1"
                    defaultValue=""
                    onChange={(e) => {
                      agregarFuente(e.target.value);
                      e.target.value = "";
                    }}
                  >
                    <option value="">+ Seleccionar encuesta para agregar como ola…</option>
                    {formsQ.data?.map((form) => (
                      <option key={form.xmlFormId} value={form.xmlFormId}>
                        {form.name} ({form.xmlFormId})
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Ajustes de sincronización */}
              <div className="pt-3 border-t border-grid grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="flex items-center gap-2 cursor-pointer pt-2">
                  <input
                    type="checkbox"
                    checked={actualizacionAutomatica}
                    onChange={(e) => setActualizacionAutomatica(e.target.checked)}
                  />
                  <span>Actualización automática en segundo plano</span>
                </label>
                <div>
                  <label className="etiqueta">Intervalo de sincronización (minutos)</label>
                  <input
                    type="number"
                    className="input"
                    min={10}
                    max={1440}
                    value={intervaloMinutos}
                    onChange={(e) => setIntervaloMinutos(Number(e.target.value))}
                  />
                </div>
              </div>
            </div>

            <div className="pt-4 border-t border-grid flex justify-end gap-2">
              <button
                type="button"
                className="btn-sec text-xs"
                onClick={() => setModalAbierto(false)}
              >
                Cancelar
              </button>
              <button
                type="button"
                className="btn text-xs"
                onClick={() => guardarMutation.mutate()}
                disabled={guardarMutation.isPending}
              >
                {guardarMutation.isPending ? "Guardando…" : datasetEditando ? "Guardar cambios" : "Crear y compilar"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
